import React from "react";
import { Interview } from "@/services/interviewService";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

// Format date to be more readable in a shorter format
export const formatDate = (date: Date | string | null | undefined) => {
  try {
    // Handle null, undefined, or empty string dates
    if (!date || (typeof date === "string" && date.trim() === "")) {
      return ""; // Return empty string for no date
    }

    const dateObj = date instanceof Date ? date : new Date(date);

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return "";
    }

    // Use date-fns for consistent formatting with the DateTimePicker
    return format(dateObj, "MM/dd h:mm a");
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

// Format currency
export const formatCurrency = (amount: number | string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(Number(amount));
};

// Get badge variant based on status
export const getStatusBadge = (status: string) => {
  switch (status) {
    case "scheduled":
      return (
        <Badge
          variant="outline"
          className="bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700"
        >
          Scheduled
        </Badge>
      );
    case "completed":
      return (
        <Badge
          variant="outline"
          className="bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700"
        >
          Completed
        </Badge>
      );
    case "canceled":
      return (
        <Badge
          variant="outline"
          className="bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700"
        >
          Canceled
        </Badge>
      );
    case "cancelled w/ fee":
      return (
        <Badge
          variant="outline"
          className="bg-orange-50 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700"
        >
          Cancelled w/ fee
        </Badge>
      );
    case "in progress":
      return (
        <Badge
          variant="outline"
          className="bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700"
        >
          In Progress
        </Badge>
      );
    case "follow-up":
      return (
        <Badge
          variant="outline"
          className="bg-purple-50 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700"
        >
          Follow-up
        </Badge>
      );
    case "follow-up scheduled":
      return (
        <Badge
          variant="outline"
          className="bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700"
        >
          Follow-up Scheduled
        </Badge>
      );
    case "follow-up completed":
      return (
        <Badge
          variant="outline"
          className="bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700"
        >
          Follow-up Completed
        </Badge>
      );
    case "secondary report":
      return (
        <Badge
          variant="outline"
          className="bg-teal-50 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-700"
        >
          Secondary Report
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

// Map field names to database field names
export const mapFieldToDbField = (field: keyof Interview): string => {
  const fieldMap: Record<string, string> = {
    id: "interview_id",
    expertName: "expert_name",
    company: "expert_company",
    title: "expert_title",
    date: "interview_date",
    duration: "interview_duration",
    projectName: "project_id",
    status: "interview_status",
    initialInterviewCost: "initial_interview_cost",
    network: "network_source",
    anonymizedAttribution: "anonymized_attribution",
    interviewDescription: "interview_description",
    questionnaireCost: "questionnaire_cost",
    expertRating: "expert_rating",
  };

  return fieldMap[field] || field;
};
